/**
 * « Examen blanc URGENT »: the session mock exams an admin sent to the learner's exam centre
 * (prepared by the AI from the themes other candidates reported after their TCF Canada). Shown at
 * the top of the home page until the learner has finished them; start → the player.
 */

import {
  ApiError,
  type UrgentMock,
  getAcademyAttemptsListQueryKey,
  getAcademyUrgentListQueryKey,
  useAcademyMocksCreate,
  useAcademyUrgentList,
} from "@agencyos/api-client";
import { Button } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { MapPin, Play, RotateCcw, Siren } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { IELTS, TCF, contentLanguage, examParams, useActiveExam } from "./ielts/exam";
import { ExamStructure } from "./MocksPage";
import { dateText } from "./shared";

/** The urgent mock exams of the learner (empty while loading or without a learner space). */
export function useUrgentMocks(enabled = true): UrgentMock[] {
  const { exam } = useActiveExam();
  const query = useAcademyUrgentList(examParams(exam), { query: { retry: false, enabled, staleTime: 60_000 } });
  return query.data?.status === 200 ? query.data.data : [];
}

/** « télétravail, logement » → ["télétravail", "logement"]. */
export function sessionThemes(value: string): string[] {
  return value
    .split(/[,;·]/)
    .map((s) => s.trim())
    .filter(Boolean);
}

function UrgentCard({ mock, locked }: { mock: UrgentMock; locked: boolean }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const create = useAcademyMocksCreate();
  const { exam } = useActiveExam();
  const [starting, setStarting] = useState(false);
  const themes = sessionThemes(mock.session_theme);
  const error = create.error instanceof ApiError ? (create.error.detail ?? t("common.error")) : create.error ? t("common.error") : null;
  const titleId = `urgent-${mock.id}`;

  const start = () => {
    setStarting(true);
    create.mutate(
      { data: { mock: mock.id } },
      {
        onSuccess: (response) => {
          if (response.status !== 201) return;
          void queryClient.invalidateQueries({ queryKey: getAcademyAttemptsListQueryKey() });
          void queryClient.invalidateQueries({ queryKey: getAcademyUrgentListQueryKey(examParams(exam)) });
          navigate(`/academy/attempt/${response.data.id}`);
        },
        onSettled: () => setStarting(false),
      },
    );
  };

  return (
    <section className="ac-urgent" aria-labelledby={titleId}>
      <div className="ac-urgent__glow" aria-hidden="true" />
      <div className="ac-urgent__main">
        <p className="ac-urgent__flag">
          <Siren aria-hidden size={15} strokeWidth={2.2} />
          <span>{t("academy.urgent.flag")}</span>
        </p>
        <h2 id={titleId} className="ac-urgent__title" lang={contentLanguage(exam)}>
          {mock.title}
        </h2>
        <p className="ac-urgent__meta">
          <span className="ac-urgent__center">
            <MapPin aria-hidden size={14} /> {mock.center || t("academy.urgent.allCenters")}
          </span>
          <span className="ac-caption">{t("academy.urgent.sentOn", { date: dateText(mock.sent_at, i18n.language) })}</span>
        </p>
        <p className="ac-urgent__intro">{exam === IELTS ? t("ielts.urgent.intro") : exam === TCF ? t("academy.urgent.intro") : t("academy.urgent.introExam", { exam: t(`ielts.exams.${exam}.name`) })}</p>
        {themes.length > 0 && (
          <div className="ac-urgent__block">
            <h3 className="ac-label">{t("academy.urgent.themes")}</h3>
            <ul className="ac-chips" aria-label={t("academy.urgent.themes")}>
              {themes.map((theme) => (
                <li key={theme} className="ac-chip ac-chip--danger" lang={contentLanguage(exam)}>
                  {theme}
                </li>
              ))}
            </ul>
          </div>
        )}
        {locked && <p className="ac-footnote">{t("academy.train.locked")}</p>}
        {error && (
          <p className="ko" role="alert">
            {error}
          </p>
        )}
        <div className="ac-urgent__actions">
          {mock.in_progress ? (
            <Button size="lg" icon={<RotateCcw aria-hidden size={18} />} onClick={() => navigate(`/academy/attempt/${mock.in_progress ?? ""}`)}>
              {t("academy.urgent.resume")}
            </Button>
          ) : (
            <Button size="lg" icon={<Play aria-hidden size={18} />} disabled={locked || starting} onClick={start}>
              {starting ? t("common.loading") : t("academy.urgent.start")}
            </Button>
          )}
        </div>
      </div>
      <div className="ac-urgent__side">
        <h3 className="ac-label">{t("academy.mocks.structure")}</h3>
        <ExamStructure compact />
        <p className="ac-urgent__rule" role="note">
          {t("academy.urgent.rule")}
        </p>
      </div>
    </section>
  );
}

/** Every urgent mock exam of the learner, most recent first (nothing when there is none). */
export function UrgentMocks({ locked }: { locked: boolean }) {
  const rows = useUrgentMocks();
  if (!rows.length) return null;
  return (
    <>
      {rows.map((mock) => (
        <UrgentCard key={mock.id} mock={mock} locked={locked} />
      ))}
    </>
  );
}
